import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, ChevronDown, Star, Facebook, MessageCircle, Crown } from 'lucide-react';
import { featuredVideo, aiModels } from '../data/mockData';
import { Button } from '../components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';

const Dashboard = () => {
  const [banglaVideoOpen, setBanglaVideoOpen] = useState(false);
  const [englishVideoOpen, setEnglishVideoOpen] = useState(false);

  return (
    <div className="space-y-6">
      {/* Featured Training Video Banner */}
      <div className="relative rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 via-pink-500 to-pink-400 p-1">
        <div className="bg-gradient-to-r from-purple-900/90 via-pink-900/80 to-pink-800/70 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-purple-600 flex items-center justify-center">
              <Play size={28} className="text-white fill-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Star size={14} className="text-yellow-400 fill-yellow-400" />
                <span className="text-yellow-400 text-sm">Featured Training Video</span>
              </div>
              <h3 className="text-white font-medium text-lg">
                {featuredVideo.title}
              </h3>
            </div>
          </div>
          <Button
            variant="outline"
            className="bg-white text-purple-600 hover:bg-gray-100 border-0 font-medium"
          >
            <Play size={16} className="mr-2 fill-purple-600" />
            Watch Now
          </Button>
        </div>
      </div>

      {/* Welcome Section */}
      <div className="rounded-xl overflow-hidden">
        <div className="bg-gradient-to-br from-purple-800 via-purple-700 to-orange-600 p-8 text-center">
          <h1 className="text-4xl font-bold text-white mb-4 italic">
            Welcome to CONNECT AI Magic
          </h1>
          <p className="text-gray-200 mb-2">
            This is the main dashboard where you can see recently added content.
          </p>
          <p className="text-gray-200 mb-8">
            To find prompts and tutorials, head on over to the libraries.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <Link to="/prompts">
              <Button className="bg-[#1a1a2e] hover:bg-[#252540] text-white px-6 py-2 font-medium">
                ALL PROMPTS
              </Button>
            </Link>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer">
              <Button className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 font-medium">
                <Facebook size={16} className="mr-2" />
                FB Group
              </Button>
            </a>
            <Button className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 font-medium">
              <MessageCircle size={16} className="mr-2" />
              Support
            </Button>
            <Link to="/credits">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 font-medium">
                <Crown size={16} className="mr-2" />
                Upgrade
              </Button>
            </Link>
          </div>

          {/* Tour Video Dropdowns */}
          <div className="flex flex-wrap justify-center gap-4">
            <Collapsible open={banglaVideoOpen} onOpenChange={setBanglaVideoOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="outline"
                  className="bg-transparent border border-gray-500 text-white hover:bg-white/10 px-6"
                >
                  <Play size={16} className="mr-2" />
                  Watch Tour Video (Bangla)
                  <ChevronDown
                    size={16}
                    className={`ml-2 transition-transform ${banglaVideoOpen ? 'rotate-180' : ''}`}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="bg-black/50 rounded-lg p-4">
                  <div className="aspect-video bg-gray-800 rounded-lg flex items-center justify-center">
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                      title="Tour Video Bangla"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="rounded-lg"
                    ></iframe>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <Collapsible open={englishVideoOpen} onOpenChange={setEnglishVideoOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="outline"
                  className="bg-transparent border border-gray-500 text-white hover:bg-white/10 px-6"
                >
                  <Play size={16} className="mr-2" />
                  Watch Tour Video (English)
                  <ChevronDown
                    size={16}
                    className={`ml-2 transition-transform ${englishVideoOpen ? 'rotate-180' : ''}`}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="bg-black/50 rounded-lg p-4">
                  <div className="aspect-video bg-gray-800 rounded-lg flex items-center justify-center">
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                      title="Tour Video English"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="rounded-lg"
                    ></iframe>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </div>

      {/* AI Models Section */}
      <div className="mt-8">
        <h2 className="text-2xl font-bold text-white text-center mb-2">
          Which AI Model to Use?
        </h2>
        <p className="text-gray-400 text-center mb-6">
          Want to know which tool or model is performing best for a specific task? Here's our current opinion.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {aiModels.map((model) => (
            <div
              key={model.id}
              className="bg-[#1a1a2e] rounded-xl p-5 border border-gray-800 hover:border-purple-500/50 transition-colors"
            >
              <h3 className="text-white font-semibold mb-2">{model.name}</h3>
              <p className="text-purple-400 text-sm mb-2">{model.category}</p>
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={14}
                    className={i < model.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}
                  />
                ))}
              </div>
              <p className="text-gray-400 text-sm">{model.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
